import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TenantsListPageDto } from 'app/api/models/tenant/tenantsListPageDto';
import { AuthRepository } from 'app/state/auth.repository';
import { IConfirm } from 'app/state/tenants.repository';
import { LocalStorageService } from 'app/api/services/local-storage-service';

@Component({
  selector: 'app-tenant-list',
  templateUrl: './tenant-list.component.html',
  styleUrls: ['./tenant-list.component.scss'],
  standalone: false,
})
export class TenantListComponent implements OnInit {
  @Input() innerWidth = 0;
  @Input() set tenants(items: TenantsListPageDto[]) {
    this.originalTenants = items;
    const ids = this.getPinnedIds();
    this.originalTenants.forEach((t) => {
      if (t.id) t.pinned = ids.has(t.id);
    });
    this.applyFilters();
  }
  @Input() isDeleteDisabled = true;
  @Input() selectedIds: string[] = [];
  @Output() selectedIdsChange = new EventEmitter<string[]>();
  @Output() delete = new EventEmitter<string>();
  @Output() toggleDisabled = new EventEmitter<TenantsListPageDto>();
  @Output() clone = new EventEmitter<IConfirm>();
  deleteConfirmation: TenantsListPageDto | null = null;
  cloneConfirmation: TenantsListPageDto | null = null;
  initTenants: TenantsListPageDto[] = [];
  originalTenants: TenantsListPageDto[] = [];
  descriptions: any[] = [];

  @Input() viewMode: 'list' | 'card' = 'list';
  filterGroup = 'allTenants';
  sortField: 'name' | 'created' | null = null;
  sortDirection: 'asc' | 'desc' = 'asc';

  private readonly PINNED_KEY = 'pinnedTenants';

  constructor(
    public auth: AuthRepository,
    private storage: LocalStorageService,
  ) {}

  ngOnInit(): void {
    this.restorePinned();
  }

  private getPinnedIds(): Set<string> {
    const raw = localStorage.getItem(this.PINNED_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  }

  private savePinnedIds(ids: Set<string>): void {
    localStorage.setItem(this.PINNED_KEY, JSON.stringify([...ids]));
  }

  private restorePinned(): void {
    const ids = this.getPinnedIds();
    this.originalTenants.forEach((t) => {
      if (t.id) t.pinned = ids.has(t.id);
    });
    this.applyFilters();
  }

  handleDeleteClick(tenant: TenantsListPageDto) {
    if (!this.isDeleteDisabled) {
      this.delete.emit(tenant.id);
    }
  }

  getInitials(name: string): string {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) {
      return '?';
    }
    return words
      .slice(0, 2)
      .map((word) => word.charAt(0).toUpperCase())
      .join('');
  }

  onPin(tenant: TenantsListPageDto) {
    tenant.pinned = !tenant.pinned;
    const ids = this.getPinnedIds();
    if (tenant.pinned && tenant.id) {
      ids.add(tenant.id);
    } else if (tenant.id) {
      ids.delete(tenant.id);
    }
    this.savePinnedIds(ids);
    this.applyFilters();
  }

  onSort(field: 'name' | 'created') {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }
    this.applyFilters();
  }

  applyFilters() {
    const pinned = this.originalTenants.filter((t) => t.pinned);
    const unpinned = this.originalTenants.filter((t) => !t.pinned);

    if (this.sortField) {
      const comparator = (a: TenantsListPageDto, b: TenantsListPageDto) =>
        this.compareTenants(a, b);
      pinned.sort(comparator);
      unpinned.sort(comparator);
    }

    this.initTenants = [...pinned, ...unpinned];
  }

  private compareTenants(a: TenantsListPageDto, b: TenantsListPageDto): number {
    const direction = this.sortDirection === 'asc' ? 1 : -1;

    if (this.sortField === 'name') {
      return a.name.localeCompare(b.name) * direction;
    }

    return (
      (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()) *
      direction
    );
  }

  isSelected(tenant: TenantsListPageDto): boolean {
    return this.selectedIds.includes(tenant.id);
  }

  toggleSelection(tenant: TenantsListPageDto) {
    this.selectedIdsChange.emit(
      this.isSelected(tenant)
        ? this.selectedIds.filter((id) => id !== tenant.id)
        : [...this.selectedIds, tenant.id],
    );
  }

  isActiveTenant(tenant: TenantsListPageDto): boolean {
    return (
      tenant.id?.toLowerCase() === this.storage.getTenantId()?.toLowerCase()
    );
  }

  private get selectableTenants(): TenantsListPageDto[] {
    return this.initTenants.filter((t) => !this.isActiveTenant(t));
  }

  isAllSelected(): boolean {
    const selectable = this.selectableTenants;
    return (
      selectable.length > 0 &&
      selectable.every((t) => this.selectedIds.includes(t.id))
    );
  }

  toggleSelectAll() {
    this.selectedIdsChange.emit(
      this.isAllSelected() ? [] : this.selectableTenants.map((t) => t.id),
    );
  }

  formatText(text: string): string {
    return text.toLowerCase().replace(/\s+/g, '-');
  }
}
